/**
 * GA/GTAG tracking — same steps as main project (lead_open, lead_form_step_1_submit, etc.).
 * In dev mode, logs the same funnel metadata in DevOverlay after real dataLayer/gtag calls.
 */
import {
  initGtag,
  trackLeadOpen,
  trackLeadSubmitAttempt,
  trackLeadSubmitSuccess,
  trackLeadSubmitError,
  trackLeadFormStepSubmit,
  trackLeadFormConversion,
  pushLeadFormModalOpen,
  pushBostFunnelLeadFieldInteraction,
  readFunnelScore,
} from '../plugins/gtag'
import type { ContactMeOpenDetail } from '../types'

function getPropertyIdFromContext(context?: ContactMeOpenDetail['context']): string | undefined {
  if (!context || typeof context !== 'object') return undefined
  const id =
    (context as Record<string, unknown>).propertyId ??
    (context as Record<string, unknown>).property_id
  return id != null ? String(id) : undefined
}

function getDevOverlay() {
  return import.meta.env.DEV ? (window as any).__bostDevOverlay : null
}

export function useContactMeAnalytics() {
  const init = (gtagId?: string) => {
    if (gtagId) initGtag(gtagId)
  }

  const trackOpen = (detail?: ContactMeOpenDetail) => {
    const propertyId = getPropertyIdFromContext(detail?.context)
    const devOverlay = getDevOverlay()
    trackLeadOpen(propertyId)
    if (devOverlay) {
      devOverlay.addEvent('lead_open', 'contact', propertyId, undefined, {
        funnel_step: 7,
        score_delta: 0,
        funnel_score: readFunnelScore(),
      })
    }
    pushLeadFormModalOpen(propertyId ? { propertyId } : undefined)
    if (devOverlay) {
      devOverlay.addEvent('lead_form_modal_open', 'dataLayer', propertyId, undefined, {
        funnel_step: 7,
        score_delta: 40,
        funnel_score: readFunnelScore(),
      })
    }
  }

  const trackFieldFirstInteraction = (fieldName: string) => {
    pushBostFunnelLeadFieldInteraction(fieldName)
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('bost_funnel_lead_field_interaction', 'funnel', fieldName, undefined, {
        funnel_step: 8,
        score_delta: 15,
        funnel_score: readFunnelScore(),
        field_name: fieldName,
      })
    }
  }

  const trackStep1Submit = (detail?: ContactMeOpenDetail) => {
    const propertyId = getPropertyIdFromContext(detail?.context)
    trackLeadFormStepSubmit(1, { propertyId })
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent(
        'lead_form_step_1_submit',
        'lead_form',
        propertyId ? `property:${propertyId}` : undefined,
        undefined,
        { funnel_step: 8, score_delta: 0, funnel_score: readFunnelScore() }
      )
    }
  }

  const trackStep2Submit = (propertyId?: string) => {
    trackLeadFormStepSubmit(2, { propertyId })
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent(
        'lead_form_step_2_submit',
        'lead_form',
        propertyId ? `property:${propertyId}` : undefined,
        undefined,
        { funnel_step: 8, score_delta: 0, funnel_score: readFunnelScore() }
      )
    }
  }

  const trackSubmitAttempt = (propertyId?: string) => {
    trackLeadSubmitAttempt(propertyId)
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_submit_attempt', 'contact', propertyId, undefined, {
        funnel_step: 8,
        score_delta: 0,
        funnel_score: readFunnelScore(),
      })
    }
  }

  const trackSubmitSuccess = (propertyId?: string) => {
    trackLeadSubmitSuccess(propertyId)
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_submit_success', 'contact', propertyId, undefined, {
        funnel_score: readFunnelScore(),
      })
    }
  }

  const trackSubmitError = (ctx: { propertyId?: string; code?: string }) => {
    const label = [
      ctx?.propertyId ? `prop=${ctx.propertyId}` : null,
      ctx?.code ? `code=${ctx.code}` : null,
    ]
      .filter(Boolean)
      .join(';')
    trackLeadSubmitError(ctx)
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_submit_error', 'contact', label || undefined, undefined, {
        funnel_step: 8,
        score_delta: 0,
        funnel_score: readFunnelScore(),
      })
    }
  }

  const trackConversion = (ctx: { conversationId?: string; propertyId?: string }) => {
    trackLeadFormConversion(ctx)
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_form_conversion', 'lead_form', ctx.conversationId, undefined, {
        funnel_step: 9,
        score_delta: 300,
        funnel_score: readFunnelScore(),
      })
    }
  }

  return {
    init,
    trackOpen,
    trackFieldFirstInteraction,
    trackStep1Submit,
    trackStep2Submit,
    trackSubmitAttempt,
    trackSubmitSuccess,
    trackSubmitError,
    trackConversion,
  }
}
